/**
 * Frame mode: this copy of the widget runs inside an iframe whose parent page
 * also runs ReadingVox (same root domain, or an origin in data-frame-origins).
 * Instead of showing a second button and toolbar, it becomes the parent's
 * agent for this frame: sends its text, highlights words as the parent reads,
 * and passes selections / click-and-listen to the parent's player. It makes no
 * API calls of its own.
 *
 * If the parent doesn't answer (no ReadingVox there, or not allowed), the
 * widget starts normally.
 */
import { FrameAgent } from './FrameAgent';
import { FRAME_PROTOCOL_VERSION, RV_FRAME, isFrameMessage, originAllowed, type ToChild, type ToParent } from './protocol';

const HANDSHAKE_MS = 1500;

/** Origin of the page embedding this frame, if the browser tells us */
function parentOrigin(): string | null {
  try {
    const ancestors = (location as Location & { ancestorOrigins?: DOMStringList }).ancestorOrigins;
    if (ancestors && ancestors.length > 0) return ancestors[0];
  } catch { /* ignore */ }
  try {
    if (document.referrer) return new URL(document.referrer).origin;
  } catch { /* ignore */ }
  return null;
}

/**
 * After the handshake timed out (the parent's widget starts late, e.g. created
 * by the app after the frame loaded): wait for the parent to invite this
 * frame. `onInvite` should take down this widget's own UI and call
 * startFrameMode again. Returns a function that stops listening.
 */
export function listenForInvite(opts: { frameOrigins?: string[] }, onInvite: () => void): () => void {
  if (!inIframe()) return () => {};
  const origin = parentOrigin();
  if (!origin || !originAllowed(origin, location.hostname, opts.frameOrigins)) return () => {};
  const onMessage = (e: MessageEvent) => {
    if (e.source !== window.parent || e.origin !== origin || !isFrameMessage(e.data)) return;
    const msg = e.data as ToChild;
    if (msg.kind !== 'invite' || (msg as { v?: number }).v !== FRAME_PROTOCOL_VERSION) return;
    stop();
    onInvite();
  };
  const stop = () => window.removeEventListener('message', onMessage);
  window.addEventListener('message', onMessage);
  return stop;
}

export function inIframe(): boolean {
  try { return window.self !== window.top; } catch { return true; }
}

/**
 * Try to join the parent page as a frame agent. Resolves true if the parent
 * acknowledged (this widget must then not build its own UI).
 */
export function startFrameMode(opts: { frameOrigins?: string[]; ignoreSelectors?: string[]; debug?: boolean }): Promise<boolean> {
  if (!inIframe()) return Promise.resolve(false);
  const origin = parentOrigin();
  if (!origin || !originAllowed(origin, location.hostname, opts.frameOrigins)) return Promise.resolve(false);
  const parent = window.parent;

  return new Promise((resolve) => {
    let agent: FrameAgent | null = null;
    const send = (msg: ToParent) => { try { parent.postMessage(msg, origin); } catch { /* parent gone */ } };

    const onMessage = (e: MessageEvent) => {
      if (e.source !== parent || e.origin !== origin || !isFrameMessage(e.data)) return;
      const msg = e.data as ToChild;
      if (msg.kind === 'ack') {
        if (agent) return;
        clearTimeout(timer);
        clearInterval(retry);
        agent = new FrameAgent(document, {
          text: (text) => send({ rv: RV_FRAME, kind: 'text', text }),
          selection: (text, rect) => send({ rv: RV_FRAME, kind: 'selection', text, rect }),
          speak: (text) => send({ rv: RV_FRAME, kind: 'speak', text }),
        }, { ignoreSelectors: opts.ignoreSelectors });
        send({ rv: RV_FRAME, kind: 'text', text: agent.currentText });
        if (opts.debug) console.log('[ReadingVox] Frame mode: reading through', origin);
        resolve(true);
        return;
      }
      if (!agent) return;
      switch (msg.kind) {
        case 'highlightWord': agent.highlightWord(msg.info); break;
        case 'highlightSentence': agent.highlightSentence(msg.sentence); break;
        case 'clear': agent.clear(); break;
        case 'style': agent.setStyle(msg.style || {}, msg.autoScroll !== false); break;
        case 'clickListen': agent.setClickListen(!!msg.on); break;
        case 'clearSelection': agent.clearSelection(); break;
      }
    };

    window.addEventListener('message', onMessage);
    // The parent's widget may still be starting: say hello a few times
    const hello = () => send({ rv: RV_FRAME, v: FRAME_PROTOCOL_VERSION, kind: 'hello' });
    const retry = setInterval(hello, 300);
    const timer = setTimeout(() => {
      clearInterval(retry);
      if (agent) return;
      window.removeEventListener('message', onMessage);
      resolve(false);
    }, HANDSHAKE_MS);
    hello();
  });
}
